import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getArtistById, getAllArtists } from '../data/dataLoader';
import { handleImageError } from '../utils/imageHelper';
import { decodeHtmlEntitiesSimple } from '../utils/htmlDecoder';
import { artistDescriptionToPlainText, sanitizeArtistDescription } from '../utils/artistDescription';
import { isDinnerSpeaker } from '../utils/actTypeHelper';
import SEO from '../components/SEO';
import ArtistDetailSkeleton from '../components/ArtistDetailSkeleton';
import './ArtistDetail.css';

function ArtistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [relatedArtists, setRelatedArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Star rating display
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < rating ? 'star filled' : 'star'}>
          ⭐
        </span>
      );
    }
    return stars;
  };

  const handleBookNow = () => {
    navigate(`/contact?artist=${encodeURIComponent(decodeHtmlEntitiesSimple(artist.name))}`);
  };

  const handleWatchVideo = (e) => {
    e.preventDefault();
    const videoSection = document.getElementById('video-section');
    if (videoSection) {
      videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    // Scroll to top immediately when page loads
    window.scrollTo(0, 0);
    
    const loadArtist = async () => {
      setLoading(true);
      const startTime = Date.now();
      const minLoadingTime = 500; // Minimum 500ms to show skeleton
      
      try {
        const foundArtist = await getArtistById(id);
        setArtist(foundArtist);

        if (foundArtist) {
          const allArtists = await getAllArtists();
          const speaker = isDinnerSpeaker(foundArtist);
          const related = allArtists
            .filter((a) => {
              if (String(a.id) === String(foundArtist.id)) return false;
              if (speaker) return isDinnerSpeaker(a);
              return a.genre === foundArtist.genre && !isDinnerSpeaker(a);
            })
            .slice(0, 3);
          setRelatedArtists(related);
        }
      } catch (error) {
        console.error('Error loading artist:', error);
      } finally {
        // Ensure minimum loading time for better UX
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, minLoadingTime - elapsed);
        setTimeout(() => {
          setLoading(false);
        }, remaining);
      }
    };

    loadArtist();
  }, [id]);

  if (loading) {
    return (
      <div className="artist-detail-page">
        <SEO 
          title="Loading..."
          description="Loading act details..."
          url={`/artist/${id}`}
        />
        <ArtistDetailSkeleton />
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="artist-not-found">
        <h2>Act Not Found</h2>
        <p>Sorry, we couldn't find the act you're looking for.</p>
        <Link to="/artists" className="btn btn-primary">Back to Performance Acts</Link>
      </div>
    );
  }

  const videoId = getYouTubeVideoId(artist.videoUrl);
  const artistIdentifier = artist.slug || artist.id;
  const artistName = decodeHtmlEntitiesSimple(artist.name);
  const tribute = decodeHtmlEntitiesSimple(artist.tribute);
  const hasDistinctTribute = tribute && tribute.trim().toLowerCase() !== artistName.trim().toLowerCase();
  const dinnerSpeaker = isDinnerSpeaker(artist);

  const performanceFeatures = [
    {
      icon: '🎭',
      title: 'Professional Performance',
      description: 'Experienced performers who deliver an authentic and memorable show',
    },
    {
      icon: '🎤',
      title: 'High-Quality Sound',
      description: 'Professional equipment and vocals that capture the original artist perfectly',
    },
    {
      icon: '🎉',
      title: 'Crowd Pleasers',
      description: 'Guaranteed to get your guests dancing and singing along',
    },
  ];

  const speakerFeatures = [
    {
      icon: '🎤',
      title: 'Engaging Presenter',
      description: 'Captivates your audience with humour, stories, and professional delivery',
    },
    {
      icon: '🏢',
      title: 'Corporate & Events',
      description: 'Ideal for awards dinners, corporate galas, and special occasions',
    },
    {
      icon: '⭐',
      title: 'Trusted Professional',
      description: 'Experienced speakers who understand the room and leave a lasting impression',
    },
  ];

  const featureCards = dinnerSpeaker ? speakerFeatures : performanceFeatures;

  return (
    <div className="artist-detail-page">
      <SEO 
        title={decodeHtmlEntitiesSimple(artist.name)}
        description={artistDescriptionToPlainText(artist.description || (dinnerSpeaker
          ? `${artist.name} - Professional dinner speaker available for booking in Scotland. Perfect for corporate events, awards dinners, and special occasions.`
          : `${artist.name} - Professional tribute act available for booking in Scotland. ${artist.genre ? `Specializing in ${artist.genre} music.` : ''} Perfect for weddings, corporate events, and private parties.`))}
        url={`/artist/${artistIdentifier}`}
        image={artist.image}
        type="article"
        artist={artist}
      />
      <div className="artist-detail-top">
        <div className="section-container">
          <div className="breadcrumb">
            <Link to="/">Home</Link> /{' '}
            <Link to={dinnerSpeaker ? '/dinner-speakers' : '/artists'}>
              {dinnerSpeaker ? 'Dinner Speakers' : 'Acts'}
            </Link>{' '}
            / <span>{artistName}</span>
          </div>
        </div>
      </div>

      <section className="artist-profile-layout">
        <div className="section-container artist-profile-grid">
          <div className="artist-profile-media">
            <img
              src={artist.image}
              alt={artistName}
              className="artist-hero-image"
              onError={(e) => handleImageError(e, artist)}
            />
          </div>

          <div className="artist-profile-content">
            <div className="artist-profile-header">
              {artist.featured && <span className="featured-badge-hero">⭐ Featured Act</span>}
              <h1 className="detail-artist-name">{artistName}</h1>
              {hasDistinctTribute && <p className="detail-tribute">{tribute}</p>}
              {artist.rating && (
                <div className="detail-rating">
                  {renderStars(artist.rating)}
                  <span className="rating-number">({artist.rating}/5)</span>
                  {artist.bookingCount && <span className="booking-count"> • {artist.bookingCount} bookings</span>}
                </div>
              )}

              {videoId && (
                <div className="profile-actions">
                  <button onClick={handleWatchVideo} className="btn btn-large btn-outline">
                    {dinnerSpeaker ? '🎬 Watch Clip' : '🎬 Watch Performance'}
                  </button>
                </div>
              )}
            </div>

            <div
              className="about-text"
              dangerouslySetInnerHTML={{
                __html: sanitizeArtistDescription(artist.description),
              }}
            />

          </div>
        </div>
      </section>

      {/* Video Section */}
      {videoId && (
        <section className="video-section" id="video-section">
          <div className="section-container">
            <h2 className="section-title">
              Watch {decodeHtmlEntitiesSimple(artist.name)} {dinnerSpeaker ? 'in Action' : 'Perform'}
            </h2>
            <div className="video-wrapper">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title={`${decodeHtmlEntitiesSimple(artist.name)} Performance`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="youtube-video"
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* Why Book This Act */}
      <section className="why-book-section">
        <div className="section-container">
          <h2 className="section-title">Why Book {decodeHtmlEntitiesSimple(artist.name)}?</h2>
          <div className="features-grid-detail">
            {featureCards.map((feature) => (
              <div key={feature.title} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2>Ready to Book {decodeHtmlEntitiesSimple(artist.name)}?</h2>
          <p>Get in touch today for availability and pricing</p>
          <div className="cta-buttons">
            <button onClick={handleBookNow} className="btn btn-large btn-cta-primary">
              ✉️ Request a Quote
            </button>
            <a href="tel:01418490333" className="btn btn-large btn-cta-secondary">
              ☎️ Call: 0141 849 0333
            </a>
          </div>
        </div>
      </section>

      {/* Related Artists */}
      {relatedArtists.length > 0 && (
        <section className="related-artists-section">
          <div className="section-container">
            <h3 className="section-title">
              {dinnerSpeaker ? 'Other Dinner Speakers You Might Like' : 'Similar Acts You Might Like'}
            </h3>
            <div className="related-artists-grid">
              {relatedArtists.map(relatedArtist => (
                <Link 
                  key={relatedArtist.id} 
                  to={`/artist/${relatedArtist.slug || relatedArtist.id}`}
                  className="related-artist-card"
                >
                  <div className="related-image-wrapper">
                    <img 
                      src={relatedArtist.image} 
                      alt={relatedArtist.name}
                      onError={(e) => handleImageError(e, relatedArtist)}
                    />
                  </div>
                  <div className="related-artist-info">
                    <h4>{relatedArtist.name}</h4>
                    <p>{relatedArtist.tribute}</p>
                    {relatedArtist.rating && (
                      <div className="related-rating">
                        {renderStars(relatedArtist.rating)}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ArtistDetail;
